import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const HeroSection = ({ events }) => {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);

  // Auto-rotate carousel every 15 seconds
  useEffect(() => {
    if (!events || events.length === 0) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % events.length);
    }, 15000); // 15 seconds

    return () => clearInterval(interval);
  }, [events]);

  if (!events || events.length === 0) return null;

  const event = events[currentIndex];

  const handleBookNow = (e) => {
    e.stopPropagation();
    navigate(`/events/${event.id}`);
  };

  const handleDotClick = (index) => {
    setCurrentIndex(index);
  };

  // Custom date formatter
  const formatDate = (dateStr) => {
    if (!dateStr) return '';

    let date;
    // Handle Firestore Timestamp
    if (dateStr && typeof dateStr.toDate === 'function') {
      date = dateStr.toDate();
    } else {
      date = new Date(dateStr);
    }

    // Check for invalid date
    if (isNaN(date.getTime())) return '';

    const dateOptions = { weekday: 'short', day: 'numeric', month: 'long' };
    const timeOptions = { hour: 'numeric', minute: 'numeric', hour12: true };

    try {
      return `${date.toLocaleDateString('en-US', dateOptions)} | ${date.toLocaleTimeString('en-US', timeOptions)} Onwards`;
    } catch (e) {
      console.error("Date formatting error:", e);
      return '';
    }
  };

  return (
    <div className="relative w-full max-w-[1200px] mx-auto">
      <div
        className="relative h-[320px] sm:h-[380px] lg:h-[420px] rounded-[20px] overflow-hidden cursor-pointer group"
        onClick={() => navigate(`/events/${event.id}`)}
      >
        {/* Background Image - Visible with reduced opacity */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-all duration-700"
          style={{
            backgroundImage: `url(${event.poster?.horizontalUrl || event.poster?.verticalUrl})`,
          }}
        />

        {/* Dark gradient overlay from left to right */}
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(to right, rgba(20, 30, 48, 0.95) 0%, rgba(20, 30, 48, 0.90) 50%, rgba(20, 30, 48, 0.75) 100%)'
          }}
        />

        {/* Content Container */}
        <div className="relative h-full flex items-center justify-between px-6 sm:px-10 lg:px-14 gap-6">

          {/* Left Content: Text - EXACT LAYOUT FROM REFERENCE */}
          <div className="flex-1 flex flex-col justify-center items-start max-w-[520px] z-10">

            {/* Event Name - Large, Bold, White */}
            <h1 className="text-white font-['Poppins'] text-[26px] sm:text-[32px] lg:text-[38px] font-semibold leading-[1.15] mb-4">
              {event.name}
            </h1>

            {/* Date & Time - Single Line, White */}
            <p className="text-white font-['Poppins'] text-[14px] sm:text-[15px] lg:text-[16px] font-normal mb-1.5">
              {formatDate(event.startTime)}
            </p>

            {/* Venue - Separate Line Below Date, White */}
            <p className="text-white/95 font-['Poppins'] text-[14px] sm:text-[15px] lg:text-[16px] font-normal mb-5">
              {event.eventLocation?.locationText || 'Venue TBA'}
            </p>

            {/* Book Now Button - Green */}
            <button
              onClick={handleBookNow}
              className="bg-[#17BD3B] hover:bg-[#15a834] text-white font-['Poppins'] font-medium px-8 py-2.5 rounded-[8px] transition-all duration-300 text-[14px] sm:text-[15px]"
            >
              Book Now
            </button>
          </div>

          {/* Right Content: Poster Card */}
          <div className="hidden lg:block relative w-[240px] h-[340px] rounded-[16px] overflow-hidden shadow-2xl flex-shrink-0">
            <img
              src={event.poster?.verticalUrl || event.poster?.horizontalUrl}
              alt={event.name}
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </div>

      {/* Slide Indicators - Clickable */}
      <div className="flex justify-center gap-2 mt-5">
        {events.map((_, index) => (
          <div
            key={index}
            onClick={() => handleDotClick(index)}
            className={`cursor-pointer transition-all duration-300 rounded-full ${index === currentIndex
              ? 'w-7 h-1.5 bg-[#17BD3B]'
              : 'w-1.5 h-1.5 bg-gray-500 hover:bg-gray-400'
              }`}
          />
        ))}
      </div>
    </div>
  );
};

export default HeroSection;
